// ── Integrations → Client Mail ──────────────────────────────────────────────
//
// The mailbox that writes to BORROWERS. Configuration only — the sending lives
// on Client Invoice Notices, under servicing, where the work happens.
//
// WHY THIS IS A SECOND ROW AND NOT A SETTING ON THE NOTIFICATION EMAIL PAGE.
// The m365send row carries allowdomains = the lender's own domain. That list is the
// last line of defence against a bug in a recipient query mailing somebody
// outside the firm — it is why a draw notice physically cannot reach a
// borrower today. Client mail has to reach borrower domains, so it cannot pass
// that check, and widening the list would buy one feature at the cost of
// removing that protection from every internal notification at the same time.
//
// So: two integration rows, two mailboxes, two Exchange scopes, two fences.
// graphMail.js takes a provider argument and each path passes its own. Neither
// can send as the other's mailbox; switching one off does not affect the other.
//
// Setup runbook: doc/m365-client-mail-setup.md.

import { org } from "../../../shared/org";
import { useState, useEffect, useCallback } from "react";
import { Btn, Card, ErrorMsg, SectionHeader, Spinner } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

const PROVIDER = "m365client";
const SECRET   = "m365client-clientsecret";

const row  = { display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 12 };
const lbl  = { width: 190, fontSize: 13, fontWeight: 500, paddingTop: 7, flex: "0 0 auto" };
const inp  = { flex: 1, padding: "7px 10px", fontSize: 13, borderRadius: 6,
               border: "1px solid var(--color-border-secondary, #ccd3d8)",
               background: "var(--color-background-primary, #fff)",
               color: "var(--color-text-primary, #1a1a1a)", boxSizing: "border-box" };
const hint = { fontSize: 11.5, color: "var(--color-text-secondary)", marginTop: 4, lineHeight: 1.5 };

const banner = (tone) => ({
  margin: "0 0 14px", padding: "10px 13px", borderRadius: 7, fontSize: 13, lineHeight: 1.55,
  background: tone === "bad" ? "#f7d9cf" : tone === "warn" ? "#fdeccd" : "#dff3ec",
  color:      tone === "bad" ? "#993C1D" : tone === "warn" ? "#7a4f00" : "#0F6E56",
});

export const IntegrationClientMailPage = () => {
  const { api, go } = useApp();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState(null);
  const [msg, setMsg]         = useState(null);

  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [sender, setSender]     = useState("");
  const [domains, setDomains]   = useState("");
  const [active, setActive]     = useState(false);
  const [secret, setSecret]     = useState("");
  const [status, setStatus]     = useState({});
  const [configured, setConfigured] = useState(false);

  // What the wording POINTS AT, as opposed to the wording itself. Kept beside
  // the mailbox because a licensee changing their portal domain should change
  // one field, not every paragraph that happens to mention it.
  const [portalUrl, setPortalUrl]   = useState("");
  const [supportEmail, setSupport]  = useState("");
  const [orgName, setOrgName]       = useState("");
  const [keepSent, setKeepSent]     = useState(true);
  // Whether that tick reflects something SAVED, or just the default it is
  // drawn with. Without this the control lies: a box nobody has ever written
  // to the database looks exactly like one that has, and the only symptom is
  // an empty Sent Items folder with no explanation anywhere.
  const [keepSentSaved, setKeepSentSaved] = useState(false);

  // The wording. Slots, defaults, overrides and a rendered preview all come
  // from the server, because the server is what renders the real thing.
  const [copy, setCopy]     = useState(null);
  const [draft, setDraft]   = useState({});   // slot -> edited text, or null to clear
  const [openSlot, setOpen] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const d = await api.get("getIntegrations", { provider: PROVIDER });
      const c = (d && d.config) || {};
      setTenantId(c.tenantid || "");
      setClientId(c.clientid || "");
      setSender(c.sender || "");
      setDomains((c.allowdomains || []).join(", "));
      setActive(!!(d && d.isactive));
      setStatus((d && d.secretstatus) || {});
      setConfigured(!!(d && d.config && d.config.sender));
      setPortalUrl(c.portalUrl || "");
      setSupport(c.supportEmail || "");
      setOrgName(c.orgName || "");
      // Defaults to on for a row that has never been saved. Client notices are
      // external correspondence and the filed copy is the only place the
      // rendered message survives — ACE records who was written to, not what
      // they actually received.
      setKeepSent(c.savetosentitems !== false);
      setKeepSentSaved(typeof c.savetosentitems === "boolean");
    } catch {
      // No row yet is the normal state before setup, not an error. The form has
      // to render in order to create it.
      setConfigured(false);
    }
    try { setCopy(await api.get("getClientNoticeCopy")); }
    catch { setCopy(null); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const secrets = {};
      // Omitted when blank — that is what makes "leave blank to keep" work
      // rather than overwriting a good secret with an empty string.
      if (secret.trim()) secrets[SECRET] = secret.trim();

      const res = await api.post("saveIntegration", {
        provider: PROVIDER,
        displayname: "Microsoft 365 — client-facing mail",
        isactive: active,
        config: {
          tenantid: tenantId.trim(), clientid: clientId.trim(),
          secretname: SECRET, sender: sender.trim(),
          allowdomains: domains.split(",").map(s => s.trim().replace(/^@/, "")).filter(Boolean),
          // Blank means "keep using the shipped value" rather than "send an
          // empty link", which is why these become undefined rather than ''.
          portalUrl: portalUrl.trim() || undefined,
          supportEmail: supportEmail.trim() || undefined,
          orgName: orgName.trim() || undefined,
          savetosentitems: keepSent,
        },
        secrets,
      });
      setStatus(res.secretstatus || status);
      setSecret("");
      setMsg({ tone: "ok", text: "Saved." });
      load();
    } catch (e) { setMsg({ tone: "bad", text: e.message }); }
    setBusy(false);
  };

  const saveSlot = async (slot, text) => {
    setBusy(true); setMsg(null);
    try {
      // text === null clears the override. Sending null rather than omitting it
      // is what the endpoint reads as "restore the default".
      await api.post("saveClientNoticeCopy", { slot, text });
      setDraft(d => { const n = { ...d }; delete n[slot]; return n; });
      setMsg({ tone: "ok", text: text === null ? "Restored to the shipped wording." : "Saved." });
      // Reloaded rather than patched, because the preview has to be re-rendered
      // by the code that sends.
      try { setCopy(await api.get("getClientNoticeCopy")); } catch { /* keep what we had */ }
    } catch (e) { setMsg({ tone: "bad", text: e.message }); }
    setBusy(false);
  };

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const secretSet = !!status[SECRET];
  const fenced = domains.trim().length > 0;

  return <>
    <SectionHeader title="Client Mail" />

    {msg && <div style={banner(msg.tone)}>{msg.text}</div>}

    {!configured && (
      <div style={banner("warn")}>
        <strong>Not set up yet.</strong> Until this is saved, Client Invoice Notices cannot send —
        its Send button stays disabled rather than failing halfway through a batch.
      </div>
    )}

    {configured && !active && (
      <div style={banner("bad")}>
        <strong>Sending is switched off.</strong> Unlike the internal notifications queue, nothing
        accumulates while this is off — client notices are sent by a button, not drained from a
        queue, so there is no backlog waiting to go out when it is switched back on.
      </div>
    )}

    <Card style={{ padding: 18, marginBottom: 16 }}>
      <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 16, lineHeight: 1.6 }}>
        The mailbox that writes to <strong>borrowers</strong>. Separate from the notifications
        mailbox on purpose: that one is fenced to <code>{org("emailDomain")}</code>, and widening it
        to reach clients would remove that protection from every internal notice as well.
        <br />
        Sending itself is on{" "}
        <a href="#" onClick={e => { e.preventDefault(); go("client-invoice-notices"); }}
           style={{ color: "var(--color-text-link, #0F62FE)" }}>Client Invoice Notices</a>,
        under Statements — this page only says who it sends as.
        <br />
        Setup runbook: <code>doc/m365-client-mail-setup.md</code>. It is the notifications runbook
        with the names changed, plus a section on deliverability that internal mail never needed.
      </div>

      <div style={row}>
        <label style={lbl}>Directory (tenant) ID</label>
        <div style={{ flex: 1 }}>
          <input value={tenantId} onChange={e => setTenantId(e.target.value)}
                 placeholder="00000000-0000-0000-0000-000000000000" style={inp} />
        </div>
      </div>

      <div style={row}>
        <label style={lbl}>Application (client) ID</label>
        <div style={{ flex: 1 }}>
          <input value={clientId} onChange={e => setClientId(e.target.value)}
                 placeholder="00000000-0000-0000-0000-000000000000" style={inp} />
          <div style={hint}>The <strong>ACE-ClientMail</strong> registration — not ACE-Notifications
            and not the mail-capture one.</div>
        </div>
      </div>

      <div style={row}>
        <label style={lbl}>
          Client secret {secretSet && <span style={{ color: "#0F6E56", fontWeight: 500 }}>✓ set</span>}
        </label>
        <div style={{ flex: 1 }}>
          <input type="password" value={secret} onChange={e => setSecret(e.target.value)}
                 placeholder={secretSet ? "•••••••••  set — leave blank to keep" : "Not set"} style={inp} />
          <div style={hint}>
            Written to Key Vault as <code>{SECRET}</code> and never read back here. When a batch
            starts failing with “Entra rejected the app credentials”, the secret has expired and
            this is the field to refill.
          </div>
        </div>
      </div>

      <div style={row}>
        <label style={lbl}>Send from</label>
        <div style={{ flex: 1 }}>
          <input value={sender} onChange={e => setSender(e.target.value)}
                 placeholder={"noreply@" + org("emailDomain")} style={inp} />
          <div style={hint}>
            Must be the mailbox the Exchange scope covers, or Graph answers 403 — and that error
            names Exchange, not Entra. Its display name is what borrowers see in their inbox, so
            it is worth setting to the firm's name rather than the mailbox's.
          </div>
        </div>
      </div>

      <div style={row}>
        <label style={lbl}>Only send to</label>
        <div style={{ flex: 1 }}>
          <input value={domains} onChange={e => setDomains(e.target.value)}
                 placeholder="leave empty to allow any borrower domain" style={inp} />
          <div style={hint}>
            {fenced
              ? <>Currently fenced to <strong>{domains}</strong>. Anything outside is refused as
                  <code> domain_blocked</code>. Useful for a rehearsal — a real borrower cannot be
                  reached while this is set — but it will block the live batch if left in place.</>
              : <><strong>Empty, so the check is off.</strong> That is normally right here:
                  borrowers are on their own domains and there is no list to maintain. It does mean
                  the recipient query is the only thing deciding who gets written to, which is why
                  that query lives in one view that both the preview and the send read.</>}
          </div>
        </div>
      </div>

      <div style={{ borderTop: "1px solid var(--color-border-tertiary, #e6e9eb)",
                    margin: "18px 0 14px", paddingTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>What the notice points at</div>
        <div style={{ ...hint, marginBottom: 12 }}>
          These fill <code>{"{portalLink}"}</code>, <code>{"{supportEmail}"}</code> and{" "}
          <code>{"{orgName}"}</code> in the wording below. Leave one blank to keep the shipped
          value. Changing the portal address here changes it in every paragraph that links to it.
        </div>

        <div style={row}>
          <label style={lbl}>Client portal address</label>
          <div style={{ flex: 1 }}>
            <input value={portalUrl} onChange={e => setPortalUrl(e.target.value)}
                   placeholder={org("portalUrlExample")} style={inp} />
            <div style={hint}>
              Where the notice sends borrowers to read their statement. Include
              <code> https://</code> — it goes into the email as a link, and a bare domain will not
              open from some mail clients.
            </div>
          </div>
        </div>

        <div style={row}>
          <label style={lbl}>Support address</label>
          <div style={{ flex: 1 }}>
            <input value={supportEmail} onChange={e => setSupport(e.target.value)}
                   placeholder={"support@" + org("emailDomain")} style={inp} />
            <div style={hint}>
              Printed in every notice as where to go when the portal will not let a borrower in.
              It has to be a mailbox somebody actually reads — the sending mailbox is not monitored
              and the notice says so.
            </div>
          </div>
        </div>

        <div style={row}>
          <label style={lbl}>Sign-off name</label>
          <div style={{ flex: 1 }}>
            <input value={orgName} onChange={e => setOrgName(e.target.value)}
                   placeholder={org("short")} style={inp} />
            <div style={hint}>The name under “Regards,”, as the borrower reads it.</div>
          </div>
        </div>
      </div>

      <div style={{ ...row, alignItems: "flex-start" }}>
        <label style={{ ...lbl, paddingTop: 0 }}>Keep a copy</label>
        <div style={{ flex: 1 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={keepSent}
                   onChange={e => { setKeepSent(e.target.checked); setKeepSentSaved(false); }} />
            <span>File each notice in the mailbox's Sent Items</span>
            {!keepSentSaved && (
              <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 4,
                             background: "#fdeccd", color: "#7a4f00" }}>
                not saved
              </span>
            )}
          </label>
          {!keepSentSaved && (
            <div style={{ fontSize: 12, color: "#7a4f00", marginTop: 6, lineHeight: 1.5 }}>
              <strong>This tick is the default, not a stored setting.</strong> Until you press Save
              below, the mailbox has no instruction either way and Graph files nothing — which
              looks exactly like the feature being broken. Press Save.
            </div>
          )}
          <div style={hint}>
            On, because this is correspondence with clients and ACE records <em>who</em> was
            written to rather than what they received — with this off, nothing anywhere holds the
            message a borrower actually got. The internal notifications mailbox keeps it off for
            the opposite reason: there the outbox is the record, and a Sent Items folder growing by
            one item per draw is somebody's cleanup job.
          </div>
        </div>
      </div>

      <div style={{ ...row, alignItems: "center" }}>
        <label style={{ ...lbl, paddingTop: 0 }}>Active</label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
          <span>Allow client notices to be sent</span>
        </label>
      </div>

      <div style={{ marginTop: 6 }}>
        <Btn variant="primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Btn>
      </div>
    </Card>

    {/* ── The wording ───────────────────────────────────────────────────── */}
    <Card style={{ padding: 18, marginBottom: 16 }}>
      <SectionHeader title="Wording" />

      {!copy ? (
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
          Not available — run <code>sql/ace_client_notice_copy.sql</code> and reload.
        </div>
      ) : <>
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 12, lineHeight: 1.6 }}>
          What the borrower reads. An empty box means the shipped wording is in use, shown greyed
          behind the cursor — clearing a box you had edited restores that default rather than
          sending a blank paragraph.
          <br />
          The month is never typed here. It comes from the period chosen on the send page and is
          substituted into <code>{"{month}"}</code>, so the one thing that must not be wrong cannot
          be got wrong twice.
        </div>

        {!copy.installed && (
          <div style={{ ...banner("warn"), margin: "0 0 12px" }}>
            <strong>Not migrated yet.</strong> Every paragraph below is running on its shipped
            wording, which is what it is actually doing. Run
            <code> sql/ace_client_notice_copy.sql</code> to be able to change it.
          </div>
        )}

        <div style={{ marginBottom: 12, padding: "9px 11px", borderRadius: 7,
                      background: "var(--color-background-secondary, #f5f7f8)" }}>
          <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 6 }}>Placeholders</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {(copy.tokens || []).map(([t, desc]) => (
              <span key={t} title={desc}
                    style={{ fontFamily: "var(--font-mono)", fontSize: 11, padding: "2px 6px",
                             borderRadius: 4, background: "var(--color-background-primary, #fff)",
                             border: "1px solid var(--color-border-tertiary, #e6e9eb)" }}>
                {`{${t}}`}
              </span>
            ))}
          </div>
          {copy.settings && (
            <div style={{ ...hint, marginTop: 7 }}>
              Right now <code>{"{portalLink}"}</code> is <strong>{copy.settings.portalUrl}</strong>
              {" "}and <code>{"{supportEmail}"}</code> is <strong>{copy.settings.supportEmail}</strong>.
              {copy.settings.usingFallback && " Both are still the shipped values — set them above."}
            </div>
          )}
          <div style={{ ...hint, marginTop: 7 }}>
            Anything else is plain text — HTML tags will show as tags rather than formatting. A
            misspelt placeholder stays visible in the preview instead of disappearing, so a typo is
            something you can see.
          </div>
        </div>

        {(copy.slots || []).map(sl => {
          const edited = sl.key in draft;
          const value  = edited ? (draft[sl.key] ?? "") : (sl.override == null ? "" : sl.override);
          const overridden = edited ? draft[sl.key] !== null : sl.override != null;
          const open = openSlot === sl.key;
          return (
            <div key={sl.key} style={{ borderTop: "1px solid var(--color-border-tertiary, #e6e9eb)",
                                       padding: "10px 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{sl.label}</span>
                {overridden && <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 7px",
                                              borderRadius: 4, background: "#fdeccd", color: "#7a4f00" }}>edited</span>}
                {sl.required && <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                  required</span>}
                <span style={{ marginLeft: "auto" }}>
                  <Btn onClick={() => setOpen(open ? null : sl.key)} disabled={busy}>
                    {open ? "Close" : "Edit"}
                  </Btn>
                </span>
              </div>
              {!open && (
                <div style={{ ...hint, marginTop: 4, whiteSpace: "pre-wrap" }}>
                  {(overridden ? value : sl.default).slice(0, 160)}
                  {(overridden ? value : sl.default).length > 160 ? "…" : ""}
                </div>
              )}
              {open && (
                <div style={{ marginTop: 10 }}>
                  {sl.multiline
                    ? <textarea value={value} disabled={busy}
                        style={{ ...inp, width: "100%", minHeight: 90, resize: "vertical",
                                 fontFamily: "inherit", lineHeight: 1.5 }}
                        placeholder={sl.default}
                        onChange={e => setDraft(d => ({ ...d, [sl.key]: e.target.value }))} />
                    : <input value={value} disabled={busy}
                        style={{ ...inp, width: "100%" }} placeholder={sl.default}
                        onChange={e => setDraft(d => ({ ...d, [sl.key]: e.target.value }))} />}
                  <div style={hint}>{sl.help}</div>
                  <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                    <Btn variant="primary" disabled={busy || !edited}
                         onClick={() => saveSlot(sl.key, draft[sl.key])}>Save</Btn>
                    {overridden && (
                      <Btn disabled={busy} onClick={() => saveSlot(sl.key, null)}>Use default</Btn>
                    )}
                    {edited && <span style={{ fontSize: 12, color: "#7a4f00" }}>Unsaved.</span>}
                    {sl.updatedby && !edited && (
                      <span style={{ fontSize: 11.5, color: "var(--color-text-secondary)" }}>
                        Last edited by {sl.updatedby}
                        {sl.updatedat ? ` on ${String(sl.updatedat).replace("T", " ").slice(0, 16)}` : ""}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <div style={{ borderTop: "1px dashed var(--color-border-tertiary, #e6e9eb)",
                      marginTop: 14, paddingTop: 12 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 12.5, fontWeight: 600 }}>Preview</span>
            <span style={{ fontSize: 11.5, color: "var(--color-text-secondary)" }}>
              sample borrower, as currently saved — save to refresh
            </span>
          </div>
          {copy.preview && copy.preview.error ? (
            <div style={{ ...banner("bad"), margin: 0 }}>
              This notice will not render: {copy.preview.error}
            </div>
          ) : copy.preview ? (
            <>
              <div style={{ fontSize: 12.5, marginBottom: 8 }}>
                <span style={{ color: "var(--color-text-secondary)" }}>Subject: </span>
                <strong>{copy.preview.subject}</strong>
              </div>
              {/* An iframe because this is EMAIL html. Dropped into the page it
                  would inherit the app's CSS and stop looking like what arrives. */}
              <iframe title="client-notice-preview" srcDoc={copy.preview.html} sandbox=""
                      style={{ width: "100%", height: 460, borderRadius: 7, background: "#fff",
                               border: "1px solid var(--color-border-tertiary, #e6e9eb)" }} />
            </>
          ) : null}
        </div>
      </>}
    </Card>

    <Card style={{ padding: 18 }}>
      <SectionHeader title="How this differs from Notification Email" />
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <tbody>
            {[["Goes to", "Staff", "Borrowers"],
              ["Mailbox", "notices@" + org("emailDomain"), "noreply@" + org("emailDomain")],
              ["Registration", "ACE-Notifications", "ACE-ClientMail"],
              ["Allowed domains", org("emailDomain"), "normally none"],
              ["Triggered by", "An event, drained on a schedule", "A button, after review"],
              ["Switched off means", "Queues and waits", "Cannot send at all"]].map(([k, a, b]) => (
              <tr key={k} style={{ borderTop: "1px solid var(--color-border-tertiary, #e6e9eb)" }}>
                <td style={{ padding: "7px 14px 7px 0", color: "var(--color-text-secondary)",
                             whiteSpace: "nowrap" }}>{k}</td>
                <td style={{ padding: "7px 14px 7px 0" }}>{a}</td>
                <td style={{ padding: "7px 0", fontWeight: 600 }}>{b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  </>;
};
